import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';
import { Form, Formik } from 'formik';
import { getServerSession } from 'next-auth/next';
import { signIn } from 'next-auth/react';
import { i18n, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Router from 'next/router';
import { useState } from 'react';
import { useSWRConfig } from 'swr';
import * as Yup from 'yup';
import { OAuthButtonGroup } from '../../components/auth/OAuthButtonGroup';

import { DarkLogo } from '../../components/layouts/navbar/DarkLogo';
import { LightLogo } from '../../components/layouts/navbar/LightLogo';
import AffiliateInitScript from '../../components/utils/AffiliateInitScript';
import { HTTP_POST } from '../../components/utils/const';
import FormikControl from '../../components/utils/FormikControl';
import { checkCookie } from '../../lib/common';
import { authOptions } from '../api/auth/[...nextauth]';

const RegisterPage = () => {
  const currentLocale = i18n.language === 'en' ? '' : '/' + i18n.language;
  //translation i18next
  const { t } = useTranslation(['auth', 'common', 'validation']);
  const invalidEmailText = t('validation:email.invalid');
  const requiredText = t('validation:required-field');
  const termsConditionsText = t('validation:terms-conditions');
  const createText = t('auth:register.create-account');
  const haveAccountText = t('auth:register.have-an-account');
  const logInText = t('common:log-in');
  const signUpText = t('auth:sign-up');
  const passwordInvalidText = t('validation:password-or-email-invalid');
  const signInWithText = t('auth:sign-with');
  const acceptText = t('auth:register.accept');
  const passwordText = t('common:password');
  const emailText = t('common:email');
  const fullNameText = t('common:full-name');
  const registerLinkText = t('auth:register.link-text');
  const logo = useColorModeValue(<DarkLogo />, <LightLogo />);

  const [errorCredentialsSignin, setErrorCredentialsSignin] = useState(false);

  const handleSignIn = () => {
    Router.push(`${currentLocale}/auth/login`);
    return;
  };

  const { fetcher } = useSWRConfig();
  const initialValues = {
    email: '',
    password: '',
    name: '',
    termsOfService: false,
  };

  const validationSchema = Yup.object({
    email: Yup.string().email(invalidEmailText).required(requiredText),
    password: Yup.string().min(6).required(requiredText),
    name: Yup.string().required(requiredText),
    termsOfService: Yup.boolean()
      .required(termsConditionsText)
      .oneOf([true], termsConditionsText),
  });

  const onSubmit = async (values, props) => {
    const res = await fetcher?.(
      '/accounts/register/',
      {
        email: values.email,
        first_name: values.name,
        password: values.password,
      },
      HTTP_POST
    );

    if (res?.id) {
      //check cookie in order to make request
      if (checkCookie('_gr_id')) {
        //get access token to make request to backend for user to be registered as a lead
        const accessToken = await axios
          .post(`${process.env.NEXT_PUBLIC_API_URL}/accounts/token/`, {
            email: res.email,
            password: values.password,
          })
          .then((res) => {
            if (res.data) return res.data?.access_token;
          });
        if (accessToken) {
          //send request to reditus
          window.gr('track', 'conversion', { email: res.email });

          //make user lead
          await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL}/marketing/lead/`,
            {
              affiliate: 'getreditus',
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
        }
      }
      signIn('credentials', {
        email: res.email,
        password: values.password,
      });
    } else {
      if (res.status === 400) {
        if (res?.data) {
          const fields = res.data;
          for (const key in fields) {
            props.setFieldError(key, `${fields[key]}`);
          }
        }
      } else if (res.status === 401) {
        //TODO: Raise error to Sentry
      }
    }
    props.setSubmitting(false);
  };

  return (
    <>
      <AffiliateInitScript />

      <Container
        maxW="xl"
        py={{
          base: '12',
          md: '24',
        }}
        px={{
          base: '0',
          sm: '8',
        }}
      >
        <Stack spacing="8">
          <Stack spacing="6">
            {logo}
            <Stack
              spacing={{
                base: '2',
                md: '3',
              }}
              textAlign="center"
            >
              <Heading size={useBreakpointValue({ base: 'xs', md: 'sm' })}>
                {createText}
              </Heading>
              <HStack spacing="1" justify="center">
                <Text color="muted">{haveAccountText}</Text>
                <Button
                  variant="link"
                  colorScheme="blue"
                  onClick={handleSignIn}
                >
                  {logInText}
                </Button>
              </HStack>
            </Stack>
          </Stack>
          <Box
            py={{
              base: '0',
              sm: '8',
            }}
            px={{
              base: '4',
              sm: '10',
            }}
            bg={useBreakpointValue({
              base: 'transparent',
              sm: 'bg-surface',
            })}
            boxShadow={{
              base: 'none',
              sm: useColorModeValue('md', 'md-dark'),
            }}
            borderRadius={{
              base: 'none',
              sm: 'xl',
            }}
          >
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {(formik) => {
                return (
                  <Form>
                    <Stack spacing="6">
                      <Stack spacing="5">
                        <FormikControl
                          control="input"
                          type="text"
                          label={fullNameText}
                          name="name"
                        />
                        <FormikControl
                          control="input"
                          type="email"
                          label={emailText}
                          name="email"
                        />
                        <FormikControl
                          control="password"
                          label={passwordText}
                          name="password"
                        />
                      </Stack>
                      <Stack justify="center">
                        <FormikControl
                          control="checkbox"
                          name="termsOfService"
                          text={acceptText}
                          linkText={registerLinkText}
                          link="https://migrationpro.io/pages/terms-of-service"
                        />
                      </Stack>
                      <Stack spacing="6">
                        <Button
                          // variant="primary"
                          mt={4}
                          colorScheme="blue"
                          isLoading={formik.isSubmitting}
                          disabled={!formik.isValid}
                          type="submit"
                        >
                          {signUpText}
                        </Button>
                        {errorCredentialsSignin && (
                          <Text
                            fontSize="sm"
                            color="red.300"
                            textAlign="center"
                          >
                            {passwordInvalidText}
                          </Text>
                        )}
                        <HStack>
                          <Divider />
                          <Text fontSize="sm" whiteSpace="nowrap" color="muted">
                            {signInWithText}
                          </Text>
                          <Divider />
                        </HStack>
                        <OAuthButtonGroup />
                      </Stack>
                    </Stack>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Stack>
      </Container>
    </>
  );
};

export const getServerSideProps = async (context) => {
  const { req, res } = context;
  const session = await getServerSession(req, res, authOptions);

  if (session) {
    return {
      redirect: {
        permanent: false,
        destination: '/migrations',
      },
    };
  } else {
    return {
      props: {
        data: null,
        ...(await serverSideTranslations(
          context.locale,
          ['auth', 'common', 'validation'],
          null,
          context.locales
        )),
      },
    };
  }
};

export default RegisterPage;
